import { useState } from 'react';

import PropTypes from 'prop-types';
import { BsCalendar, BsXLg } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Textarea from '#globalComponents/Forms/Textarea';
import TextInput from '#globalComponents/Forms/TextInput';
import IconButton from '#globalComponents/IconButton/IconButton';
import { useErrorStore } from '#lib/stores/errorStore';

import { useCreateActivity, useUpdateActivity } from '../../../../models/activities/mutations';

import { SubModeToggle, nowLocal } from './shared';

const ActivityForm = ({ companyId, dealId, onClose, initialData }) => {
	const isEdit = Boolean(initialData);
	const [subMode, setSubMode] = useState(() =>
		isEdit ? (initialData.status === 'completed' ? 'log' : 'todo') : 'todo',
	);
	const [title, setTitle] = useState(initialData?.title ?? '');
	const [dueAt, setDueAt] = useState(
		initialData?.dueAt ? new Date(initialData.dueAt).toISOString().slice(0, 16) : '',
	);
	const [body, setBody] = useState(initialData?.body ?? '');
	const createActivity = useCreateActivity();
	const updateActivity = useUpdateActivity();
	const showError = useErrorStore((s) => s.showError);

	const handleSubmit = async () => {
		if (!title.trim()) return;
		try {
			if (isEdit) {
				await updateActivity.mutateAsync({
					id: initialData.id,
					payload: {
						title: title.trim(),
						body: body || null,
						dueAt: subMode === 'todo' && dueAt ? new Date(dueAt).toISOString() : null,
					},
				});
			} else {
				const now = new Date().toISOString();
				const base = { crmCompanyId: companyId, ...(dealId && { dealId }) };
				const payload =
					subMode === 'log'
						? {
								...base,
								activityType: 'task',
								title: title.trim(),
								body: body || null,
								status: 'completed',
								completedAt: now,
							}
						: {
								...base,
								activityType: 'task',
								title: title.trim(),
								body: body || null,
								status: 'planned',
								dueAt: dueAt ? new Date(dueAt).toISOString() : null,
							};
				await createActivity.mutateAsync(payload);
			}
			onClose();
		} catch (err) {
			showError({ error: err, title: isEdit ? 'Speichern fehlgeschlagen' : 'Fehler beim Speichern' });
		}
	};

	const isPending = isEdit ? updateActivity.isPending : createActivity.isPending;

	return (
		<div className="p-3 space-y-2">
			<div className="flex items-center gap-2">
				<div className="flex items-center gap-1.5 px-2 py-1 rounded-control bg-primary-100 text-primary-600 text-xs font-medium">
					<BsCalendar className="w-4 h-4" /> Aktivität
				</div>
				{!isEdit && <SubModeToggle value={subMode} onChange={setSubMode} />}
				<IconButton icon={BsXLg} label="Schließen" onClick={onClose} className="ml-auto h-6 w-6" />
			</div>
			<TextInput
				value={title}
				onChange={(e) => setTitle(e.target.value)}
				placeholder="Was ist zu tun?"
				autoFocus
			/>
			{subMode === 'todo' && (
				<TextInput
					type="datetime-local"
					value={dueAt}
					onChange={(e) => setDueAt(e.target.value)}
					min={nowLocal()}
					className="!w-auto"
				/>
			)}
			<Textarea
				value={body}
				onChange={(e) => setBody(e.target.value)}
				placeholder="Notiz (optional)"
				rows={2}
				className="!resize-none"
			/>
			<div className="flex justify-end gap-2 pt-1">
				<Button type="button" variant="outline" size="sm" onClick={onClose}>
					Abbrechen
				</Button>
				<Button variant="solid" size="sm" onClick={handleSubmit} loading={isPending} disabled={!title.trim()}>
					Speichern
				</Button>
			</div>
		</div>
	);
};
ActivityForm.propTypes = {
	companyId: PropTypes.string,
	dealId: PropTypes.string,
	onClose: PropTypes.func,
	initialData: PropTypes.object,
};

export default ActivityForm;
